import { cloneDeep } from 'lodash';

import { calcClientDerivatives, carePhaseDefList } from '@shared';
import { CarePhase } from '@shared/appModel';
import { computeMonthlyCostMetrics } from '@shared/calculations/funding/projection/sharedEligibility';

/**
 * Median memory-care duration for people with dementia, the time from
 * entering a memory-care facility to death (Joling et al., 2020).
 */
export const DEMENTIA_MEMORY_CARE_YEARS = 2.5;

const DEMENTIA_MEMORY_CARE_MONTHS = Math.round(DEMENTIA_MEMORY_CARE_YEARS * 12);

const FEASIBLE_SURPLUS_MARGIN = 0.1;

const phaseDefByValue = new Map(carePhaseDefList.map(def => [def.value, def]));

export type TypeCFeasibilityStatus = 'feasible' | 'close' | 'at-risk';

export interface TypeCCarePhaseSegment {
  label: string;
  years: number;
}

export interface TypeCScenario {
  projectedLtcCost: number;
  fundingResult: number;
  status: TypeCFeasibilityStatus;
  totalCareYears: number;
  phases: TypeCCarePhaseSegment[];
  fundsRunOutYear: number | null;
  fundsRunOutPhaseLabel: string | null;
  /** Position of the run-out within its phase, 0 (phase start) to 1 (phase end). */
  fundsRunOutPhaseFraction: number | null;
}

export interface TypeCResident {
  age: number | null;
  gender: string | null;
  stateAbbreviation: string | null;
}

export interface TypeCFeasibility {
  resident: TypeCResident;
  selfFundingCapacity: number;
  yearsTillLtc: number;
  expected: TypeCScenario;
  dementia: TypeCScenario;
}

function deriveClient(client: Client, fullCareMonths?: number): Client {
  const clone = cloneDeep(client);

  if (fullCareMonths != null) {
    clone.carePhaseDurationSelections = {
      ...clone.carePhaseDurationSelections,
      [CarePhase.fullCare]: fullCareMonths,
    };
  }

  return calcClientDerivatives(clone, null) as Client;
}

function blendedReturnRate(client: Client): number {
  const sources = Object.values(client.multipleFundingSources?.selfFunding ?? {});

  let weightedRate = 0;
  let totalValue = 0;

  for (const source of sources) {
    if (source.isDeactivated) {
      continue;
    }

    const value = source.selfFundingTotalValue ?? source.existingAssetsValue ?? 0;

    if (value <= 0) {
      continue;
    }

    weightedRate += value * (source.annualRateOfReturn ?? 0);
    totalValue += value;
  }

  return totalValue > 0 ? weightedRate / totalValue : 0;
}

interface SimulationResult {
  fundingResult: number;
  runOutMonth: number | null;
}

/**
 * Month-by-month drawdown of the self-funding capacity across the care period.
 * The balance compounds at the blended rate while positive, and each month's
 * inflated care cost is withdrawn.
 */
function simulate(capacity: number, annualRate: number, monthlyCosts: number[]): SimulationResult {
  const monthlyRate = Math.pow(1 + annualRate, 1 / 12) - 1;

  let balance = capacity;
  let runOutMonth: number | null = null;

  monthlyCosts.forEach((cost, month) => {
    const grown = balance > 0 ? balance * (1 + monthlyRate) : balance;

    if (runOutMonth === null && grown < cost) {
      runOutMonth = month + (grown > 0 ? grown / cost : 0);
    }

    balance = grown - cost;
  });

  return { fundingResult: balance, runOutMonth };
}

function phaseLabel(carePhase: CarePhase, isDementia: boolean): string {
  if (isDementia && carePhase === CarePhase.fullCare) {
    return 'Memory Care';
  }

  return phaseDefByValue.get(carePhase)?.label ?? 'Care';
}

function feasibilityStatus(
  fundingResult: number,
  projectedLtcCost: number,
): TypeCFeasibilityStatus {
  if (fundingResult < 0) {
    return 'at-risk';
  }

  if (projectedLtcCost > 0 && fundingResult < projectedLtcCost * FEASIBLE_SURPLUS_MARGIN) {
    return 'close';
  }

  return 'feasible';
}

function getFullCareMonths(client: Client): number {
  const phase = (client.phaseCosts ?? []).find(p => p.carePhase === CarePhase.fullCare);

  return phase ? Math.round(phase.phaseCareMonthsNeeded) : 0;
}

/** Build a scenario view-model from an already-derived client. */
function buildScenario(
  client: Client,
  capacity: number,
  annualRate: number,
  isDementia: boolean,
): TypeCScenario {
  const segments = (client.phaseCosts ?? [])
    .filter(phase => phaseDefByValue.has(phase.carePhase))
    .map(phase => ({
      carePhase: phase.carePhase,
      months: Math.max(0, Math.round(phase.phaseCareMonthsNeeded)),
      cost: Math.max(0, phase.phaseInflatedProfessionalShareCost),
    }))
    .sort(
      (a, b) =>
        (phaseDefByValue.get(a.carePhase)?.index ?? 0) -
        (phaseDefByValue.get(b.carePhase)?.index ?? 0),
    );

  const totalMonths = segments.reduce((sum, seg) => sum + seg.months, 0);
  const projectedLtcCost = segments.reduce((sum, seg) => sum + seg.cost, 0);

  const planByPhase = new Map(
    (client.projectionInputs?.phaseCarePlans ?? []).map(plan => [plan.carePhase, plan]),
  );

  // Per-month cost stream. Each phase's already-inflated total is distributed
  // across its months in proportion to the engine's annually-stepped per-month
  // inflation, so earlier months cost less than later ones instead of every
  // month carrying the phase's flat average. A flat average front-loads a
  // longer phase's later, higher-inflation cost onto its first months, which
  // drains funds too fast and moves the funds-out point earlier than the care
  // actually warrants.
  const monthlyCosts: number[] = [];

  for (const seg of segments) {
    if (seg.months <= 0) {
      continue;
    }

    const plan = planByPhase.get(seg.carePhase);
    const weights: number[] = [];
    let weightTotal = 0;

    for (let m = 0; m < seg.months; m++) {
      const weight = plan ? computeMonthlyCostMetrics(client, plan, m).effectiveCareCost : 1;
      weights.push(weight);
      weightTotal += weight;
    }

    for (let m = 0; m < seg.months; m++) {
      monthlyCosts.push(
        weightTotal > 0 ? seg.cost * (weights[m] / weightTotal) : seg.cost / seg.months,
      );
    }
  }

  const { fundingResult, runOutMonth } = simulate(capacity, annualRate, monthlyCosts);

  const phases: TypeCCarePhaseSegment[] = segments.map(seg => ({
    label: phaseLabel(seg.carePhase, isDementia),
    years: seg.months / 12,
  }));

  let fundsRunOutYear: number | null = null;
  let fundsRunOutPhaseLabel: string | null = null;
  let fundsRunOutPhaseFraction: number | null = null;

  if (runOutMonth !== null && totalMonths > 0) {
    fundsRunOutYear = runOutMonth / 12;

    let cursor = 0;

    for (const seg of segments) {
      const isLast = seg === segments[segments.length - 1];

      if (runOutMonth < cursor + seg.months || isLast) {
        fundsRunOutPhaseLabel = phaseLabel(seg.carePhase, isDementia);
        fundsRunOutPhaseFraction = seg.months > 0 ? (runOutMonth - cursor) / seg.months : 0;
        break;
      }

      cursor += seg.months;
    }
  }

  return {
    projectedLtcCost,
    fundingResult,
    status: feasibilityStatus(fundingResult, projectedLtcCost),
    totalCareYears: totalMonths / 12,
    phases,
    fundsRunOutYear,
    fundsRunOutPhaseLabel,
    fundsRunOutPhaseFraction,
  };
}

/**
 * Assess whether a CCRC Type C resident can self-fund care under the expected
 * care progression and under a dementia event, where the full-care phase
 * becomes a memory-care stay of {@link DEMENTIA_MEMORY_CARE_YEARS} years.
 */
export function computeTypeCFeasibility(client: Client): TypeCFeasibility {
  const expectedClient = deriveClient(client);

  // A dementia event sets the full-care phase to the median memory-care
  // duration. When the expected projection already has a longer full-care
  // phase, that longer duration is kept — a dementia event is never milder
  // than the expected progression.
  const dementiaFullCareMonths = Math.max(
    getFullCareMonths(expectedClient),
    DEMENTIA_MEMORY_CARE_MONTHS,
  );
  const dementiaClient = deriveClient(client, dementiaFullCareMonths);

  // Capacity and return rate come from the accumulation phase, which the
  // dementia care-duration override does not affect — compute them once.
  const capacity = expectedClient.multipleFundingSources?.multipleSelfFundingTotalValue ?? 0;
  const annualRate = blendedReturnRate(expectedClient);

  return {
    resident: {
      age: expectedClient.clientDemographics?.age ?? null,
      gender: expectedClient.clientDemographics?.gender ?? null,
      stateAbbreviation: expectedClient.clientDemographics?.stateAbbreviation ?? null,
    },
    selfFundingCapacity: capacity,
    yearsTillLtc: expectedClient.appliedInferenceSet?.yearsTillLtc ?? 0,
    expected: buildScenario(expectedClient, capacity, annualRate, false),
    dementia: buildScenario(dementiaClient, capacity, annualRate, true),
  };
}
